import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ordersApi, PageResponse, AdminOrderResponse, OrderTab, formatScheduled, selectedOptions } from '../services/ordersApi';
import OrderStatusBadge from '../components/orders/OrderStatusBadge';

const TABS: { key: OrderTab; label: string }[] = [
  { key: 'NEEDS_ACTION', label: 'Needs action' },
  { key: 'AWAITING_DRIVER', label: 'Awaiting driver' },
  { key: 'DISPUTED', label: 'Disputed' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'CLOSED', label: 'Completed / Closed' },
  { key: 'ALL', label: 'All' },
];

function shortId(id: string) {
  return id.substring(0, 8) + '…';
}

/** One line of a cell, cut with an ellipsis; the full text stays on hover. */
const ellipsis = { maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/** #1193 — the service, and under it the customer's selection (variant values or picked options). */
function ServiceCell({ order }: { order: AdminOrderResponse }) {
  const options = selectedOptions(order).map((l) => l.label).join(', ');
  return (
    <td>
      <div style={ellipsis} title={order.serviceName ?? undefined}>{order.serviceName ?? '—'}</div>
      {options && (
        <div data-testid="order-options" style={{ ...ellipsis, color: '#6b7280', fontSize: '0.75rem' }} title={options}>
          {options}
        </div>
      )}
    </td>
  );
}

export default function OrdersPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<PageResponse<AdminOrderResponse> | null>(null);
  const [loading, setLoading] = useState(true);

  // Tab + filters
  // Spec 069 — the tab lives in `?tab=` so dashboard tiles land pre-filtered and Back restores it.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as OrderTab | null;
  const [tab, setTab] = useState<OrderTab>(
    requestedTab && TABS.some((t) => t.key === requestedTab) ? requestedTab : 'NEEDS_ACTION',
  );
  const [search, setSearch] = useState('');
  const [garageId, setGarageId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // #1181 — the reserved day, and which date column the list is sorted on.
  const [scheduledFrom, setScheduledFrom] = useState('');
  const [scheduledTo, setScheduledTo] = useState('');
  const [sort, setSort] = useState('createdAt,desc');
  const [page, setPage] = useState(0);

  const fetchOrders = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    try {
      const result = await ordersApi.listOrders({
        tab,
        search: search || undefined,
        garageId: garageId || undefined,
        customerId: customerId || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        scheduledFrom: scheduledFrom || undefined,
        scheduledTo: scheduledTo || undefined,
        page,
        size: 20,
        sort,
      }, auth.token);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [auth.token, tab, search, garageId, customerId, dateFrom, dateTo, scheduledFrom, scheduledTo, sort, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleTabChange = (next: OrderTab) => {
    setTab(next);
    setPage(0);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: true });
  };

  const handleSearch = () => {
    setPage(0);
    fetchOrders();
  };

  const handleClear = () => {
    setSearch('');
    setGarageId('');
    setCustomerId('');
    setDateFrom('');
    setDateTo('');
    setScheduledFrom('');
    setScheduledTo('');
    setPage(0);
  };

  /** Click a date header: sort on it newest first, click again for oldest first. */
  const toggleSort = (field: 'createdAt' | 'scheduledDate') => {
    setSort((current) => (current === `${field},desc` ? `${field},asc` : `${field},desc`));
    setPage(0);
  };
  const sortMark = (field: string) =>
    sort === `${field},desc` ? ' ↓' : sort === `${field},asc` ? ' ↑' : '';

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', marginBottom: '1.25rem' }}>
        Orders {data && `(${data.totalElements})`}
      </h2>

      {/* Tab strip */}
      <div className="orders-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`orders-tab ${tab === t.key ? 'orders-tab--active' : ''}`}
            onClick={() => handleTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-card">
        {/* Filter bar */}
        <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search order #, customer, phone, garage, plate, or customer ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            style={{ minWidth: '320px', flex: 1 }}
          />

          <input
            type="text"
            className="admin-search-input"
            placeholder="Garage ID"
            value={garageId}
            onChange={(e) => setGarageId(e.target.value)}
            style={{ width: '180px' }}
          />

          <input
            type="text"
            className="admin-search-input"
            placeholder="Customer ID"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            style={{ width: '180px' }}
          />

          <input
            type="date"
            className="admin-search-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="Created from"
            aria-label="Created from"
          />

          <input
            type="date"
            className="admin-search-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="Created to"
            aria-label="Created to"
          />

          <input
            type="date"
            className="admin-search-input"
            value={scheduledFrom}
            onChange={(e) => { setScheduledFrom(e.target.value); setPage(0); }}
            title="Scheduled from"
            aria-label="Scheduled from"
          />

          <input
            type="date"
            className="admin-search-input"
            value={scheduledTo}
            onChange={(e) => { setScheduledTo(e.target.value); setPage(0); }}
            title="Scheduled to"
            aria-label="Scheduled to"
          />

          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={handleSearch}>
            Search
          </button>

          {(search || garageId || customerId || dateFrom || dateTo || scheduledFrom || scheduledTo) && (
            <button className="admin-btn admin-btn-outline admin-btn-sm" onClick={handleClear}>
              Clear
            </button>
          )}
        </div>

        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : !data || data.content.length === 0 ? (
          <div className="admin-empty-state">
            <p>No orders found.</p>
          </div>
        ) : (
          <>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Status</th>
                    <th>Customer</th>
                    <th>Garage</th>
                    <th>Service</th>
                    <th>Amount</th>
                    <th>
                      <button type="button" className="admin-th-sort" onClick={() => toggleSort('scheduledDate')}>
                        Scheduled{sortMark('scheduledDate')}
                      </button>
                    </th>
                    <th>
                      <button type="button" className="admin-th-sort" onClick={() => toggleSort('createdAt')}>
                        Created{sortMark('createdAt')}
                      </button>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((order) => (
                    <tr key={order.id}>
                      <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {order.orderNumber ?? shortId(order.id)}
                      </td>
                      <td>
                        <OrderStatusBadge status={order.status} />
                      </td>
                      <td title={order.customerId}>
                        {order.customerName ?? <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{shortId(order.customerId)}</span>}
                      </td>
                      <td title={order.garageId}>
                        {order.garageName ?? <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{shortId(order.garageId)}</span>}
                      </td>
                      <ServiceCell order={order} />
                      <td>{Number(order.totalAmount).toFixed(2)} {order.currency}</td>
                      <td>{formatScheduled(order.scheduledDate, order.pickupTimeSlotLabel) ?? '—'}</td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="admin-btn admin-btn-outline admin-btn-sm"
                          onClick={() => navigate(`/orders/${order.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data.totalPages > 1 && (
              <div className="admin-pagination">
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  disabled={data.number === 0}
                  onClick={() => setPage(data.number - 1)}
                >
                  Previous
                </button>
                <span style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)' }}>
                  Page {data.number + 1} of {data.totalPages}
                </span>
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  disabled={data.number + 1 >= data.totalPages}
                  onClick={() => setPage(data.number + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
